import { useQueries } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { URL_EXTENSION } from '@utils/constants/app-constants.ts';
import { DocumentResponse, Folder } from '@utils/types/folders/folder.ts';
import { BaseResponse } from '@utils/types/shared/base.ts';
import { PinnedItem } from '@utils/localStorage/pinned-items-storage.ts';

/**
 * Fetch a single folder by ID
 * Returns null if folder doesn't exist (404) instead of throwing
 */
const fetchFolderById = async (folderId: string): Promise<Folder | null> => {
  try {
    const { data } = await Axios.get<BaseResponse<Folder>>(
      `${URL_EXTENSION}/folders/${folderId}/`
    );
    return data.result;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // If folder was deleted (404), return null instead of throwing
    // This prevents errors from being logged when pinned folders are deleted
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Fetch a single document by ID and session ID
 * Documents require both session_id and document_id
 */
const fetchDocumentById = async (
  sessionId: string,
  documentId: string
): Promise<DocumentResponse | null> => {
  try {
    const { data } = await Axios.get<BaseResponse<DocumentResponse>>(
      `${URL_EXTENSION}/speech-to-text/session/${sessionId}/document/${documentId}`
    );
    return data.result;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // If document was deleted (404), return null instead of throwing
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export interface PinnedItemsData {
  folders: Folder[];
  documents: DocumentResponse[];
}

/**
 * Hook to fetch all pinned items (folders and documents)
 * This allows us to show pinned items on any page, even if they're not in current page data
 */
export const useGetPinnedItems = (
  pinnedItems: PinnedItem[],
  enabled: boolean = true
): {
  data: PinnedItemsData;
  isLoading: boolean;
  deletedItemIds: string[]; // IDs of items that returned 404
} => {
  // Separate folders and documents
  const pinnedFolders = pinnedItems.filter(item => item.type === 'folder');
  const pinnedDocuments = pinnedItems.filter(
    item => item.type === 'document' && item.sessionId
  );

  // Query for each pinned folder.
  //
  // Previously: staleTime 5s + refetchOnMount:true → N parallel HTTP calls on
  // every Workspace return, one per pinned folder, each landing at a
  // different time and rerendering the grid. Martijn's "table always
  // rebuilding the view" complaint on many-pinned setups (#2035).
  //
  // Now: staleTime 60s + no forced refetch-on-mount. Folder metadata (name,
  // last_modified) barely changes between navigations, so a minute of
  // staleness is fine here. cacheTime bumped so leaving and coming back
  // within 5 min reads from cache instantly.
  const folderQueries = useQueries(
    pinnedFolders.map(item => ({
      queryKey: ['pinned-folder', item.id],
      queryFn: () => fetchFolderById(item.id),
      enabled: enabled && !!item.id,
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onError: () => {
        // Silently fail - item might have been deleted
      }
    }))
  );

  // Query for each pinned document. While a document is in any non-terminal
  // state (Uploaded, Downloading, Processing, …) we poll every 10s so the
  // pinned row flips to Finished/Error on its own — the regular workspace list
  // refreshes via its own queries, but pinned items are a separate query and
  // would otherwise stay stale until the user filters, unpins, or remounts.
  // Polling stops once the status is terminal (Finished or Error).
  const TERMINAL_STATUSES = new Set(['Finished', 'Error']);
  const documentQueries = useQueries(
    pinnedDocuments.map(item => ({
      queryKey: ['pinned-document', item.sessionId, item.id],
      queryFn: () => fetchDocumentById(item.sessionId!, item.id),
      enabled: enabled && !!item.id && !!item.sessionId,
      // Cache-friendly like pinned folders (#2035). Terminal-status polling
      // below still keeps in-progress rows fresh — we only skip the
      // refetch-on-every-mount fanout for docs that are already done.
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchInterval: (data: DocumentResponse | null | undefined) => {
        // Before first response, poll so we don't miss a quick transition
        if (!data) return 10000;
        const status = data?.session?.status;
        return status && TERMINAL_STATUSES.has(status) ? false : 10000;
      },
      refetchIntervalInBackground: false,
      onError: () => {
        // Silently fail - item might have been deleted
      }
    }))
  );

  const isLoading =
    folderQueries.some(query => query.isLoading) ||
    documentQueries.some(query => query.isLoading);

  // Collect IDs of deleted items (ones that returned null from 404)
  const deletedItemIds: string[] = [];

  const folders = folderQueries
    .filter((query, index) => {
      if (query.data === null && !query.isLoading) {
        // This folder returned 404 - mark it for cleanup
        deletedItemIds.push(pinnedFolders[index].id);
        return false;
      }
      return query.data !== undefined;
    })
    .map(query => query.data as Folder);

  const documents = documentQueries
    .filter((query, index) => {
      if (query.data === null && !query.isLoading) {
        // This document returned 404 - mark it for cleanup
        deletedItemIds.push(pinnedDocuments[index].id);
        return false;
      }
      return query.data !== undefined;
    })
    .map(query => query.data as DocumentResponse);

  return {
    data: {
      folders,
      documents
    },
    isLoading,
    deletedItemIds
  };
};
